// Copyright 2025, University of Maryland, All Rights Reserved
import { useSearchParams } from 'react-router-dom'

import { WorkspaceGeneralSettings } from '@/app-shell/components/WorkspaceGeneralSettings'
import { WorkspaceSettingsHeader, type WorkspaceSettingsTab } from '@/app-shell/components/WorkspaceSettingsHeader'
import { WorkspaceTemplateSettings } from '@/app-shell/components/WorkspaceTemplateSettings'
import { WorkspaceVaultSettings } from '@/subsystems/vault/components/WorkspaceVaultSettings'
import { useAuth } from '@/subsystems/workspaces'

export default function WorkspaceSettingsPage() {
  const [params, setParams] = useSearchParams()
  const {
    selectedOrganizationId,
    selectedWorkspaceId,
    currentWorkspace,
    isGlobalAdmin,
    isWorkspaceAdmin,
  } = useAuth()
  const orgId = selectedOrganizationId ?? ''
  const workspaceId = selectedWorkspaceId ?? ''
  const application = currentWorkspace?.application ?? ''
  const vaultEligible = application === 'contract-review' || application === 'srm'
  const tabs: WorkspaceSettingsTab[] = [
    { id: 'general', label: 'General' },
    ...(vaultEligible ? [{ id: 'vault', label: 'Vault' }] : []),
    { id: 'advanced', label: 'Advanced' },
  ]
  const requestedSection = params.get('section')
  const activeTab = tabs.some(tab => tab.id === requestedSection)
    ? requestedSection!
    : 'general'

  if (!orgId || !workspaceId) {
    return <div className="p-6 text-center text-muted-foreground">No workspace selected.</div>
  }

  return (
    <div data-testid="workspace-settings-page" className="mx-auto max-w-[1060px] px-6 pb-14 pt-8">
      <WorkspaceSettingsHeader
        workspaceName={currentWorkspace?.name ?? workspaceId}
        applicationName={currentWorkspace?.applicationDisplayName ?? applicationName(application)}
        workspaceId={workspaceId}
        administrator={isGlobalAdmin || isWorkspaceAdmin}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={tab => {
          setParams(current => {
            current.set('section', tab)
            return current
          }, { replace: true })
        }}
      />

      <main className="mt-8">
        {activeTab === 'general' && (
          <WorkspaceGeneralSettings
            orgId={orgId}
            workspaceId={workspaceId}
            connectorEligible={vaultEligible}
          />
        )}
        {activeTab === 'vault' && (
          <WorkspaceVaultSettings
            orgId={orgId}
            workspaceId={workspaceId}
            workspaceName={currentWorkspace?.name ?? workspaceId}
            canManage={isGlobalAdmin || isWorkspaceAdmin}
          />
        )}
        {activeTab === 'advanced' && (
          <WorkspaceTemplateSettings orgId={orgId} workspaceId={workspaceId} />
        )}
      </main>
    </div>
  )
}

function applicationName(application: string) {
  return application
    .split('-')
    .map(part => part ? part[0].toUpperCase() + part.slice(1) : part)
    .join(' ')
}
